import { useCallback, useEffect, useMemo } from 'react';
import { LocationControllerApi } from '../api/generated';
import type { LocationResponse } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { useAppDispatch, useAppSelector, useAppStore } from '../store/hooks';
import {
  locationsLoadFailed,
  locationsLoadStarted,
  locationsLoaded,
  selectHasFetchedLocations,
  selectLocations,
  selectLocationsIsLoading,
  selectLocationsLoadError
} from '../store/locations/locationsSlice';
import { getErrorMessage } from '../utils/errorMessageUtils';

export interface UseLocationsResult {
  locations: LocationResponse[];
  locationsById: Record<string, LocationResponse>;
  locationsByName: Record<string, LocationResponse>;
  isLoading: boolean;
  loadError: string | null;
  // Re-reads the list, for callers that have just changed it (creating,
  // renaming, recolouring or deleting a location). Because the list is shared
  // state, this updates every consumer — the header switcher included — not
  // just the caller. Never rejects: a refresh that fails surfaces through
  // `loadError`, so a write that already succeeded is not reported as a
  // failure.
  refresh: () => Promise<void>;
}

/**
 * Access to the account's accessible locations.
 *
 * The list is fetched once and held in the locations slice, so every consumer
 * reads the same copy and re-renders together when it changes. That sharing is
 * the point: this hook has many independent callers, and while each held its
 * own copy, deleting or renaming a location on the settings page left the
 * header switcher (and every other reader) showing the old list until a reload.
 */
export function useLocations(): UseLocationsResult {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const locations = useAppSelector(selectLocations);
  const isLoading = useAppSelector(selectLocationsIsLoading);
  const loadError = useAppSelector(selectLocationsLoadError);

  const api = useMemo(() => new LocationControllerApi(createApiConfiguration()), []);

  const refresh = useCallback(async (): Promise<void> => {
    dispatch(locationsLoadStarted());
    try {
      const response = await api.getLocations();
      dispatch(locationsLoaded(response.data));
    } catch (e) {
      console.error('Failed to load locations:', e);
      dispatch(locationsLoadFailed(getErrorMessage(e, 'Failed to load locations')));
    }
  }, [api, dispatch]);

  useEffect(() => {
    // Read through the store rather than the selector values above: several
    // consumers can mount in the same commit, and each one's captured values
    // still say "not loading" until React re-renders it. getState() sees the
    // load the first of them just started, so only one request goes out.
    const state = store.getState();
    if (selectLocationsIsLoading(state)) return;
    // A list that loaded and then failed to re-read (a refresh after a delete,
    // say) is NOT usable: it is stale, and its error would otherwise stick
    // until a browser reload, since nothing else clears it. So the next
    // consumer to mount retries whenever the last read failed, whether that was
    // the first read or a later one.
    const hasUsableList =
      selectHasFetchedLocations(state) && selectLocationsLoadError(state) === null;
    if (hasUsableList) return;
    void refresh();
  }, [store, refresh]);

  const locationsById = useMemo(() => {
    const map: Record<string, LocationResponse> = {};
    for (const loc of locations) {
      map[loc.id] = loc;
    }
    return map;
  }, [locations]);

  const locationsByName = useMemo(() => {
    const map: Record<string, LocationResponse> = {};
    for (const loc of locations) {
      map[loc.name] = loc;
    }
    return map;
  }, [locations]);

  return { locations, locationsById, locationsByName, isLoading, loadError, refresh };
}
